import { useMemo, useState } from 'react';
import { useFilters, rangeISO, compareRangeFor } from '@/state/filters';
import type { ProductTaxonomyFilters } from '@/api/bi';

/**
 * Shared scope for every product page: the taxonomy rail multi-selects plus
 * the global range/store/compare slice. Bundling it here keeps the rail
 * wiring identical across Products / Brands / Categories / Forecast and lets
 * the `HierarchyMatrix` push drill-down selections straight back in.
 */
export interface ProductScope {
  // Rail state
  vendorIds: number[];
  setVendorIds: (ids: number[]) => void;
  brandIds: number[];
  setBrandIds: (ids: number[]) => void;
  categoryGroupIds: number[];
  setCategoryGroupIds: (ids: number[]) => void;
  categoryIds: number[];
  setCategoryIds: (ids: number[]) => void;
  subcategoryIds: number[];
  setSubcategoryIds: (ids: number[]) => void;
  productQuery: string;
  setProductQuery: (v: string) => void;
  clear: () => void;
  hasSelection: boolean;

  // Global slice
  storeId: number | null;
  storeIds: number[];
  iso: { start: string; end: string };
  compareEnabled: boolean;
  compareRange: { start: string; end: string } | null;

  /** Ready-to-spread taxonomy params for the product hooks. */
  taxonomy: ProductTaxonomyFilters;
}

export function useProductScope(): ProductScope {
  const range = useFilters((s) => s.range);
  const storeId = useFilters((s) => s.storeId);
  const storeIds = useFilters((s) => s.storeIds);
  const compareMode = useFilters((s) => s.compareMode);
  const timeRangeMode = useFilters((s) => s.timeRangeMode);

  const iso = useMemo(() => rangeISO(range, timeRangeMode), [range, timeRangeMode]);
  const compareEnabled = compareMode !== 'none';
  const compareRange = useMemo(
    () => compareRangeFor(range, compareMode, timeRangeMode),
    [range, compareMode, timeRangeMode],
  );

  const [vendorIds, setVendorIds] = useState<number[]>([]);
  const [brandIds, setBrandIds] = useState<number[]>([]);
  const [categoryGroupIds, setCategoryGroupIds] = useState<number[]>([]);
  const [categoryIds, setCategoryIds] = useState<number[]>([]);
  const [subcategoryIds, setSubcategoryIds] = useState<number[]>([]);
  const [productQuery, setProductQuery] = useState('');

  const clear = () => {
    setVendorIds([]);
    setBrandIds([]);
    setCategoryGroupIds([]);
    setCategoryIds([]);
    setSubcategoryIds([]);
    setProductQuery('');
  };

  const hasSelection =
    vendorIds.length > 0 ||
    brandIds.length > 0 ||
    categoryGroupIds.length > 0 ||
    categoryIds.length > 0 ||
    subcategoryIds.length > 0 ||
    productQuery.trim().length > 0;

  const taxonomy = useMemo<ProductTaxonomyFilters>(
    () => ({
      storeId,
      storeIds,
      vendorIds,
      brandIds,
      categoryGroupIds,
      categoryIds,
      subcategoryIds,
      q: productQuery.trim() || undefined,
    }),
    [storeId, storeIds, vendorIds, brandIds, categoryGroupIds, categoryIds, subcategoryIds, productQuery],
  );

  return {
    vendorIds, setVendorIds,
    brandIds, setBrandIds,
    categoryGroupIds, setCategoryGroupIds,
    categoryIds, setCategoryIds,
    subcategoryIds, setSubcategoryIds,
    productQuery, setProductQuery,
    clear, hasSelection,
    storeId, storeIds,
    iso, compareEnabled, compareRange,
    taxonomy,
  };
}
